import {Injectable} from '@angular/core';
import {HttpEvent} from '@angular/common/http';

import {Observable, of} from 'rxjs';
import {delay, switchMap} from 'rxjs/operators';

import {ScannerStatus} from '../../interfaces/scanner-api/scanner-status';
import {ScannerSignals} from '../../interfaces/scanner-api/scanner';
import {AppConfiguration, CurrentSettings, State, StateInterface} from '../../interfaces/biozoom-api/app-configuration';
import {PipelineStageConfig, StatusObject} from '../../interfaces/biozoom-api/pipeline';
import {ScannerVersionApiService} from './scanner-version-api.service';
import {isVersionEqualOrNewer} from '../../methods/helpers';

@Injectable({
  providedIn: 'root'
})

export class ScannerApiService {


  constructor(
    private scannerVersionService: ScannerVersionApiService,
  ) {
  }


  setSignalOnOff(on: ScannerSignals[], off: ScannerSignals[], state: StateInterface) {
    return this.scannerVersionService.setSignalOnOff(on, off, state);
  }

  getMeasurement(state: StateInterface, measurementType: string, oldApi = false): Observable<HttpEvent<any>> {
    return this.scannerVersionService.measure(state, measurementType, oldApi);
  }

  // doHrvOfflineMeasurement(state: StateInterface, full = true, v2: boolean): Observable<HttpEvent<any>> {
  //   return this.scannerVersionService.doHrvOfflineMeasurement(state, full, v2);
  // }
  //
  // getHrvOfflineMeasurement(state: StateInterface, v2: boolean) {
  //   const command = v2 ? 'read-flash?file=true' : 'flashReadHRV';
  //   return this.scannerVersionService.getHrvOfflineMeasurement(command, state);
  // }

  getHandrecognition(state: StateInterface, settings: CurrentSettings, handOff = false, wifiFirmware: string): Observable<boolean> {
    if (state.DEMO_MODE) {
      return of<boolean>(true).pipe(delay(1000));
    }
    return of({}).pipe(
      switchMap(async () => {
        return await this.scannerVersionService.doHandrecognitionObservable(state, settings, handOff, wifiFirmware);
      })
    );
  }

  // updateScannerFirmwareOffline(filename, state: StateInterface): Observable<string> {
  //   return this.scannerVersionService.updateScannerFirmwareOffline(filename, state);
  // }

  getIdent(state: StateInterface): Observable<string> {
    return this.scannerVersionService.getIdent(state);
  }

  getStatus(state: StateInterface): Observable<ScannerStatus> {
    return of({}).pipe(
      switchMap(async () => {
        return await this.scannerVersionService.getStatus(state);
      })
    );
  }

  disconnectScanner(state: StateInterface) {
    return this.scannerVersionService.disconnectScanner(state);
  }


  checkScannerFirmwareOffline(state: StateInterface, config: AppConfiguration): Observable<string> {
    return of({}).pipe(
      switchMap(async () => {
        return await this.scannerVersionService.checkScannerFirmware(state, config);
      }),
      delay(1000),
    );
  }

  async connectToScanner(
    state: StateInterface,
    setState: ((key: State, val: any) => void),
    setBlockScreen: ((block: boolean) => void) = (() => { }),
    usbOnly: boolean = false,
  ): Promise<[boolean, string, number]> {
    return this.scannerVersionService.connectToScanner(state, setState, setBlockScreen, usbOnly);
  }

  async deleteFlash(pipelineConfig: PipelineStageConfig) {
    while (true) {
      const r = await this.scannerVersionService.deleteFlash(pipelineConfig);
      if (r) {
        break;
      }
      // tslint:disable-next-line:no-shadowed-variable
      await new Promise((r) => setTimeout(r, 3000));
    }
    return true;
  }

  updateESP(pipelineConfig: PipelineStageConfig, downgrade = false) {
    const sendResult = (started: boolean, target: string) => {
      // tslint:disable-next-line:variable-name
      return switchMap((_stati: StatusObject): Observable<{started: boolean, target: string}> => {
        return of({
          started,
          target,
        });
      });
    };
    let method = '';
    let toggleUSB = false;
    const result = {
      started: false,
      target: 'onNotNeeded',
    };
    const x = pipelineConfig.state.persistentValues.SCANNER_STATUS ?? '';
    const major = Number(( x?.wifi_firmware ?? '0.0.0' ).split('.')[0]);
    const minor = Number(( x?.wifi_firmware ?? '0.0.0' ).split('.')[1]);

    if (pipelineConfig.state.persistentValues.DEMO_MODE) {
      result.target = 'onNotNeeded';
      return sendResult(result.started, result.target);
    }

    if (pipelineConfig.state.persistentValues.CONNECTION_TYPE === 'WIFI') {
      const statusOk = isVersionEqualOrNewer(x, pipelineConfig.config, downgrade);
      if (statusOk === 'ok' || statusOk === 'unknown') {
        result.target = 'onNotNeeded';
        return sendResult(result.started, result.target);
      }
      pipelineConfig.setState('firmware', statusOk, 'temp');
      method = 'wifi';
    } else if (pipelineConfig.state.persistentValues.USB_MODE) {
      const serial = pipelineConfig?.state?.persistentValues?.SCANNER_SERIAL ?? '';
      if ((minor > 3 || (major >= 11 && major < 20) || (major >= 21 && major < 30)) && pipelineConfig.state.persistentValues.USB_MODE) {
        method = 'usb2';
      } else {
        if (serial && serial !== '') {
          if (Number(serial.slice(2, 4)) > 20) {
            method = 'wifi';
            toggleUSB = true;
          } else {
            result.target = 'onNotNeeded';
            return sendResult(result.started, result.target);
          }
        } else {
          result.target = 'onError';
          return sendResult(result.started, result.target);
        }
      }
    }
    return this.scannerVersionService.updateESP(pipelineConfig, method, toggleUSB, downgrade);
  }

  updateNuvoton(pipelineConfig: PipelineStageConfig) {
    return this.scannerVersionService.updateNuvoton(pipelineConfig);
  }

  updateInfineon(pipelineConfig: PipelineStageConfig, bootloader: boolean) {
    return this.scannerVersionService.updateInfineon(pipelineConfig, bootloader);
  }

  executeGenericCommand(state: StateInterface, command: string, receivedData: any) {
    return this.scannerVersionService.executeGenericCommand(state, command, receivedData);
  }

  finalizeScanner(pipelineConfig: PipelineStageConfig) {
    return this.scannerVersionService.finalizeScanner(pipelineConfig);
  }
}
